import React, { useState, useEffect, useRef } from "react";
import {
    Dialog,
    Box,
    Grid,
    FormControl,
    Select,
    TextField,
    MenuItem,
    Switch,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Button,
    DialogActions,
    InputLabel,
    Snackbar,
    CircularProgress,
    Typography,
    Divider,
    InputAdornment,
    Tooltip,
    IconButton,
    useTheme,
    Alert,
    Autocomplete,
    Slide,
    Collapse,
    FormHelperText
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { format } from "date-fns";
import TipsAndUpdatesIcon from "@mui/icons-material/TipsAndUpdates";
import { useAtom } from "jotai";
import {
    bookingDetailsAtom,
    setDetailAtom,
    showBookingDetailsAtom,
    canCheckoutAtom,
    getSelectedLocationAtom,
    getSelectedServiceAtom,
    isRemoteBookingAtom,
    customTimeActionsAtom,
    providersListAtom,
    stripeChargeCardStateAtom,
    showWhoIsAvailableDialogAtom,
    whoIsAvailableSlotsAtom,
    whoIsAvailableTimezoneAtom,
    showOnlyWhoIsAvailableDialogAtom
} from "../atoms/booking";
import { ClientSelector } from "../components/ClientSelector";
import { ProviderSearch } from "../components/atomic/ProviderSearch";
import {
    allActiveLocationsAtom,
    companyAtom,
    getProviderByProviderIdAtom,
    suggestionsEnabledAtom,
    allServicesAtom,
    getProviderServicesByProviderIdAtom,
    snackBarAtom,
    companyMasterTz,
    clientPackagesAtom,
    companyCollectsPaymentAtom,
    companyCurrencyAtom,
    allActiveServicesAtom,
    isWhoIsAvailableAllowedAtom,
    onCreateBookingFunctionAtom,
    openFeatureGateDialogAtom
} from "../atoms/atoms";
import { Elements } from "@stripe/react-stripe-js";
import { listProviderSchedules } from "../modules/ScheduleQueries";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { useQuery } from "@tanstack/react-query";
import { execReadBySortkey } from "../modules/DBService";
import {
    DatePicker,
    MobileDateTimePicker,
    TimePicker
} from "@mui/x-date-pickers";
import { customLocale } from "../utils/CalendarFunctions/CalendarConfig";
import {
    _getSchedules,
    createProviderScheduleBK,
    getAWSDate,
    getScheduleTimeblockId,
    getTravelTimeValidity
} from "../modules/ScheduleService";
import {
    colors,
    DEFAULT_STRIPE_CHARGE_CARD_STATE,
    getCurrencyForCountry,
    PAY_TYPE_NOT_REQUIRED,
    SLOT_TYPES
} from "../utils/Constants";
import { addMinutes } from "date-fns";
import {
    createNewUiSession,
    deleteHeldSlots,
    getBookingTimeOfAppt,
    getPaidPackages
} from "../modules/AdhocBookingService";
import { getPrice } from "../modules/AdhocBookingService";
import { checkCardExpiration } from "../billing/BillingCommon";
import { AddressEntryTextBox } from "../components/address/AddressEntryTextBox";
import {
    CLIENT_LOCATION_ID,
    DEFAULT_BOOKING_DETAILS,
    DEFAULT_SB_FOR_CLUSTERS,
    FEATURE_NAME_CLUSTERING,
    PACKAGE_TYPE_DOLLAR,
    PAY_TYPE_CC,
    PAY_TYPE_COLLECT_LATER,
    PAY_TYPE_NEW_CC
} from "../utils/Constants";
import DeleteIcon from "@mui/icons-material/Delete";
import { graphql, graphqlOperation, postApi } from "../modules/AmplifyServices";
import {
    listRegionalPricings,
    locationByProvider,
    packageByCompany
} from "../graphql/queries";
import { RepeatingSetup } from "./RecurringSetup";
import { find, map, some, uniqBy } from "lodash";
import { PaymentForm } from "./PaymentForm";
import { getCurrentTimeAdjustedToNextHour } from "../utils/SchedulerFunctions/DateNavigatorFormatter";
import { regionalPricingByCompanyServiceType } from "../queries/BillingChargesQueries";
import { ClientAddressInput } from "../components/ClientAddressInput";
import RRuleSelector from "../components/RRuleSelector";
import AddIcon from "@mui/icons-material/Add";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { timezoneConversion } from "../utils/CalendarFunctions/timezoneFunctions";
import moment from "moment";
import isFeatureGated from "../modules/FeatureGatingModule";
import { isAddressInTravelZone } from "../utils/CalendarFunctions/geoFunctions";
import { SuggestedTimesDialog } from "./SuggestedTimesDialog";
import {
    addMinutesToTimeString,
    formatDateInfoAttributes
} from "../modules/TimeService";
import { loadStripe } from "@stripe/stripe-js";
import {
    mbRecurringBookingRRuleAtom,
    resetRecurringSettingsAtom,
    rfcRRuleAtom
} from "../atoms/recurringSettings";
import { getOrCreateProviderLocation } from "../utils/providerLocationUtils";
import LightBulbSvg from "../images/LightBulbSvg";
import { currencyFormat } from "../modules/AdhocBookingService";
import { enUS } from "date-fns/locale";
import DateTimeSelector from "../components/DateTimeSelector";
import LinkDetectionTextField from "../utils/CommonComonents/LinkDetectionTextField";
import { WhoIsAvailableDialog } from "./WhoIsAvailableDialog";
import InfoTooltip from "../utils/UI/InfoTooltip";
import { utcToZonedTime } from "date-fns-tz";
import InfoIcon from "@mui/icons-material/Info";
import { jsonParse } from "../utils";

export const BookingDialog = ({ open, onClose }) => {
    const [company] = useAtom(companyAtom);
    const [showCheckout, setShowCheckout] = useState(false);
    const [stripeObj, setStripeObj] = useState(null);
    const [, setShowBookingDetails] = useAtom(showBookingDetailsAtom);
    const [, resetRRuleSettings] = useAtom(resetRecurringSettingsAtom);
    const [, setIsStripeChargeCardValid] = useAtom(stripeChargeCardStateAtom);
    const [{ provider, heldSlots, client }, setBookingDetails] =
        useAtom(bookingDetailsAtom);
    const [, setDetail] = useAtom(setDetailAtom);
    const [onCreateBookingFunction] = useAtom(onCreateBookingFunctionAtom);
    const [showOnlyWhoIsAvailableDialog] = useAtom(
        showOnlyWhoIsAvailableDialogAtom
    );

    useQuery({
        queryKey: ["stripe-object", company?.id],
        queryFn: async () => {
            if (company?.stripeConnectEnabled && company?.stripeAccount) {
                //For Connected Account
                return await loadStripe(
                    process.env.REACT_APP_STRIPE_MB_PUB_KEY,
                    {
                        stripeAccount: company.stripeAccount
                    }
                );
            } else if (
                company?.collectpayment &&
                company?.publishableStripeKey
            ) {
                //For Not Connected Account
                return await loadStripe(company.publishableStripeKey);
            } else {
                return null;
            }
        },
        onSuccess: (stripeObj) => {
            setStripeObj(stripeObj);
        }
    });

    useQuery({
        queryKey: ["check-card-expiration", client?.stripeCustomerId],
        queryFn: async () => {
            const result = await checkCardExpiration({
                stripecustomerid: client.stripeCustomerId,
                companyId: client.companyId,
                clientEmailAddress: client.user.emailaddress,
                stripeAccount: company.stripeAccount,
                stripeConnectEnabled: company.stripeConnectEnabled
            });
            return result; //change to result
        },
        onSuccess: (result) => {
            setDetail({ cardExpired: result.cardExpired });
        },
        enabled: !!client?.stripeCustomerId
    });

    async function handleClosePaymentForm() {
        setShowCheckout(false);
        setIsStripeChargeCardValid(DEFAULT_STRIPE_CHARGE_CARD_STATE);
        if (heldSlots?.length) {
            try {
                await deleteHeldSlots(heldSlots);
                setDetail({
                    overrideServicePrice: null,
                    overrideServiceFee: null
                });
            } catch (e) {
                console.error(e);
            }
        }
    }

    function handleCloseBookingDialog() {
        setShowBookingDetails(false);
        setBookingDetails(DEFAULT_BOOKING_DETAILS);
        resetRRuleSettings();
        setIsStripeChargeCardValid(DEFAULT_STRIPE_CHARGE_CARD_STATE);
        onClose();
    }

    function handleBookingCompleted() {
        if (onCreateBookingFunction?.fn)
            onCreateBookingFunction.fn({ providerId: provider?.id });
        setShowCheckout(false);
        setBookingDetails(DEFAULT_BOOKING_DETAILS);
        setIsStripeChargeCardValid(DEFAULT_STRIPE_CHARGE_CARD_STATE);
        onClose();
    }

    return (
        <Dialog
            open={open}
            onClose={handleCloseBookingDialog}
            fullWidth
            maxWidth="lg"
            sx={{ display: showOnlyWhoIsAvailableDialog ? "none" : "block" }}
        >
            <BookingForm
                showCheckout={() => setShowCheckout(true)}
                checkoutOpen={showCheckout}
                onClose={handleCloseBookingDialog}
            />
            {showCheckout && (
                <Elements stripe={stripeObj}>
                    <PaymentForm
                        open={showCheckout}
                        onClose={handleClosePaymentForm}
                        onBookingCompleted={handleBookingCompleted}
                    />
                </Elements>
            )}
        </Dialog>
    );
};

export const BookingForm = ({ showCheckout, checkoutOpen, onClose }) => {
    const [
        {
            selectedLocationId,
            notes,
            selectedServiceId,
            showNotes,
            isRepeating,
            isBookingDateTimeOptimized,
            client,
            providerLocationIds,
            sessions,
            selectedPackage,
            selectedPackageRemainingQuantity,
            providerId,
            provider,
            isBookSeparately,
            clientAddress,
            clientAddressOneLine,
            placeId,
            schedules
        }
    ] = useAtom(bookingDetailsAtom);
    const bookingDateTime = sessions?.length ? sessions[0]?.dateTime : null;
    const [services] = useAtom(allActiveServicesAtom);
    const providerServices = useAtom(getProviderServicesByProviderIdAtom)[0](
        providerId
    );
    const [providersList] = useAtom(providersListAtom);
    const [suggestionsEnabled] = useAtom(suggestionsEnabledAtom);
    const [, setClientPackage] = useAtom(clientPackagesAtom);
    const [masterTz] = useAtom(companyMasterTz);
    const [matchedSBsForClusters, setMatchedSBsForClusters] = useState([]);
    const [company] = useAtom(companyAtom);
    const [, setDetail] = useAtom(setDetailAtom);
    const [activeLocations] = useAtom(allActiveLocationsAtom);
    const locations = [
        {
            id: "customerLocation",
            locationname: "Client address"
        },
        ...activeLocations
    ];
    const autocompleteService = { current: null };
    const [selectedLocation] = useAtom(getSelectedLocationAtom);
    const [selectedService] = useAtom(getSelectedServiceAtom);
    const [companyCurrency] = useAtom(companyCurrencyAtom);
    const [{ id: companyId }] = useAtom(companyAtom);
    const [companyCollectsPayment] = useAtom(companyCollectsPaymentAtom);
    const [showChangeRepeatingDialog, setShowChangeRepeatingDialog] =
        useState(false);
    const [showClustersDialog, setShowClustersDialog] = useState(false);
    const [showMessage, setShowMessage] = useState(false);
    const [message] = useState(snackBarAtom);
    const [showCustomDateTimeDialog, setShowCustomDateTimeDialog] =
        useState(false);
    const [openDateTimePickerView, setOpenDateTimePickerView] = useState("day");
    const [openDateTimePicker, setOpenDateTimePicker] = useState(false);
    const sessionIndexRef = useRef(null);
    const [disableElements, setDisableElements] = useState(false);
    const [locTzDate, setLocTzDate] = useState(
        moment().format("MM/DD/YYYY, h:mm A")
    );
    const dateInputFormat = "EEE, MMM dd, yyyy – h:mm aa";
    const [travelTimeWarningMessage, setTravelTimeWarningMessage] = useState();
    const clusteringIsEnabled = !isFeatureGated(
        company.subscriptionLevel,
        FEATURE_NAME_CLUSTERING
    );
    const [showLocDate, setShowLocDate] = useState(false);
    const [addressError, setAddressError] = useState(false);
    const [showBookingModal] = useAtom(showBookingDetailsAtom);

    const [serviceSelectorAutoFocus, setServiceSelectorAutoFocus] =
        useState(false);
    const serviceSelectorRef = useRef(null);
    const [clientSelectorAutoFocus, setClientSelectorAutoFocus] =
        useState(false);
    const [dateTimePickerAutoFocus, setDateTimePickerAutoFocus] =
        useState(false);
    const dateTimePickerRef = useRef(null);
    const [providerSelectorAutoFocus, setProviderSelectorAutoFocus] =
        useState(false);
    const providerSelectorRef = useRef(null);
    const clientSelectorRef = useRef(null);
    const [bookingNotesAutoFocus, setBookingNotesAutoFocus] = useState(false);
    const bookingNotesRef = useRef(null);
    const [showWhoIsAvailableDialog, setShowWhoIsAvailableDialog] = useAtom(
        showWhoIsAvailableDialogAtom
    );
    const [, setWhoIsAvailableSlots] = useAtom(whoIsAvailableSlotsAtom);
    const [whoIsAvailableTimezone, setWhoIsAvailableTimezone] = useAtom(
        whoIsAvailableTimezoneAtom
    );
    const [, setOpenFeatureGateDialog] = useAtom(openFeatureGateDialogAtom);
    /* const selectedPackageQuantity =
        selectedPackage?.status && selectedPackage?.status === "paid"
            ? selectedPackage?.initialQuantity - selectedPackage?.usedQuantity
            : selectedPackage?.quantity; */

    const now = new Date();
    const [customDateTime, setCustomDateTime] = useState({
        date: now,
        time: now
    });
    const resetCustomDateTime = () =>
        setCustomDateTime({
            date: now,
            time: now
        });

    const [, setSaveCustomTime] = useAtom(customTimeActionsAtom);
    const [agreedToOverride, setAgreedToOverride] = useState(false);
    const { data: servicePackages } = useQuery({
        queryKey: ["get-packages", selectedServiceId],
        queryFn: async () => {
            try {
                let { items, errors } = await execReadBySortkey({
                    opname: "packageByCompany",
                    op: packageByCompany,
                    id: { companyId: company.id },
                    skey: {
                        servicetypeIdCreatedAt: {
                            beginsWith: { servicetypeId: selectedServiceId }
                        }
                    },
                    filter: {
                        and: [
                            { active: { ne: false } },
                            { deleted: { ne: true } }
                        ]
                    },
                    sortDirection: "DESC"
                });
                if (errors) throw errors;
                return items || [];
            } catch (e) {
                console.log(e);
            }
        }
    });

    const { data: clientPackages } = useQuery({
        queryKey: ["get-client-packages", client, selectedService],
        queryFn: async () => {
            try {
                let paidPackages = [];
                if (client && selectedService) {
                    paidPackages = await getPaidPackages({
                        userId: client.user.id,
                        servicetypeid: selectedService.id
                    });
                }
                return paidPackages;
            } catch (e) {
                console.log(e);
            }
        },
        onSuccess: (paidPackages) => {
            setClientPackage(paidPackages);
        }
    });

    useQuery({
        queryKey: ["get-schedules", provider?.id],
        queryFn: async () => {
            if (provider?.id) {
                try {
                    const schedules = uniqBy(
                        await _getSchedules({
                            providerId: provider?.id,
                            companyId: company.id
                        }),
                        "scheduleinfo"
                    );
                    return schedules;
                } catch (e) {
                    console.log(e);
                }
            }
            return [];
        },
        onSuccess: (schedules) => {
            setDetail({ schedules });
        }
    });

    useEffect(() => {
        if (company?.currency) {
            setDetail({ currency: company.currency });
        }
    }, [company?.currency]);

    useEffect(() => {
        if (clientSelectorAutoFocus && clientSelectorRef.current) {
            clientSelectorRef.current.focus();

            //reset the focus state after focusing
            setClientSelectorAutoFocus(false);
        }
    }, [clientSelectorAutoFocus]);

    useEffect(() => {
        if (serviceSelectorAutoFocus && serviceSelectorRef.current) {
            serviceSelectorRef.current.focus();

            //reset the focus state after focusing
            setServiceSelectorAutoFocus(false);
        }
    }, [serviceSelectorAutoFocus]);

    useEffect(() => {
        if (providerSelectorAutoFocus && providerSelectorRef.current) {
            providerSelectorRef.current.focus();

            //reset the focus state after focusing
            setProviderSelectorAutoFocus(false);
        }
    }, [providerSelectorAutoFocus]);

    useEffect(() => {
        if (dateTimePickerAutoFocus && dateTimePickerRef.current) {
            dateTimePickerRef.current.focus();

            //reset the focus state after focusing
            setDateTimePickerAutoFocus(false);
        }
    }, [dateTimePickerAutoFocus]);

    useEffect(() => {
        if (bookingNotesAutoFocus && bookingNotesRef.current) {
            bookingNotesRef.current.focus();

            //reset the focus state after focusing
            setBookingNotesAutoFocus(false);
        }
    }, [bookingNotesAutoFocus]);

    useEffect(() => {
        async function doTravelTimeValidityAnalysis() {
            const travelTimeValidity = await getTravelTimeValidity({
                bookingDateTime: bookingDateTime, //bookingDate is string - coming over web
                bookingGeoLoc: {
                    lat: selectedLocation?.latitude,
                    lng: selectedLocation?.longitude
                },
                provider,
                company,
                service: selectedService
            });
            setDetail({
                isBookingDateTimeOptimized: Boolean(
                    travelTimeValidity?.travelTimeAnalysis
                )
            });
            setDetail({
                optimizationMessage:
                    travelTimeValidity?.previousBooking
                        ?.travelTimeWarningMessage ?? ""
            });
        }
        if (
            showBookingModal &&
            suggestionsEnabled &&
            selectedService &&
            selectedLocation &&
            isBookingDateTimeOptimized === null &&
            provider
        )
            doTravelTimeValidityAnalysis();
    }, [
        bookingDateTime,
        company,
        isBookingDateTimeOptimized,
        provider,
        selectedLocation,
        selectedService,
        setDetail,
        suggestionsEnabled
    ]);

    useEffect(() => {
        if (selectedLocation && selectedService) {
            determineScheduleIdForClustering();
        }
    }, [bookingDateTime, provider, selectedLocation, selectedService]);

    const [, setShowBookingDetails] = useAtom(showBookingDetailsAtom);
    const [showOnlyWhoIsAvailableDialog, setShowOnlyWhoIsAvailableDialog] =
        useAtom(showOnlyWhoIsAvailableDialogAtom);
    const [isRemoteBooking] = useAtom(isRemoteBookingAtom);

    const [canCheckout] = useAtom(canCheckoutAtom);
    const theme = useTheme();

    const [isFetching, setIsFetching] = useState(false);

    const bookingEndDateTime = addMinutes(
        bookingDateTime,
        selectedService?.minutes
    );

    const packageOptions = [
        ...(clientPackages?.map(
            ({
                id,
                package: { desc },
                initialQuantity,
                usedQuantity,
                servicetype: { price },
                package: { packagetype, discount, price: packagePrice },
                createdAt
            }) => ({
                title: `Package of ${initialQuantity} at $${packagetype === "PERCENTAGE" ? `${price * (1 - discount / 100)}` : `${packagePrice / initialQuantity}`} per session`,
                subtitle: `(${initialQuantity - usedQuantity} of ${initialQuantity} credits remaining)`,
                helperText: `Purchased ${format(new Date(createdAt), "MMM dd, yyyy")}`,
                id: id,
                label: `Package of ${initialQuantity} at $${packagetype === "PERCENTAGE" ? `${price * (1 - discount / 100)}` : `${packagePrice / initialQuantity}`} per session`,
                type: "client"
            })
        ) || []),
        ...(servicePackages?.map(
            ({
                id,
                desc,
                quantity,
                packagetype,
                price: packagePrice,
                servicetype: { price },
                discount
            }) => ({
                title: `Package of ${quantity} at $${packagetype === "PERCENTAGE" ? `${price * (1 - discount / 100)}` : `${packagePrice / quantity}`} per session`,
                id,
                label: `Package of ${quantity} at $${packagetype === "PERCENTAGE" ? `${price * (1 - discount / 100)}` : `${packagePrice / quantity}`} per session`,
                type: "service"
            })
        ) || [])
    ];

    const pulseAnimation = {
        "@keyframes pulse": {
            "0%": {
                boxShadow: `0 0 0 0px ${theme.palette.primary.main}`,
                borderRadius: "4px"
            },
            "100%": {
                boxShadow: `0 0 0 15px  ${theme.palette.transparent.main}`,
                borderRadius: "4px"
            }
        },
        animation: "pulse 2s infinite"
    };
    const [openSuggestedTimesDialog, setOpenSuggestedTimesDialog] =
        useState(false);
    const handleCloseSuggestedTimesDialog = () => {
        setOpenSuggestedTimesDialog(false);
    };
    function handleDateTimeChange(
        dateTimeValue,
        index = sessionIndexRef.current ?? 0,
        slotType = SLOT_TYPES.REGULAR_SLOT
    ) {
        let newDate = new Date(dateTimeValue);

        //Retrieve the selected dateTime formatted to providers timezone
        const {
            dtstamp_str: providerTzDateTimeIso,
            tz_abbr_disp: providerTzAbbr
        } = formatDateInfoAttributes(
            newDate,
            selectedService?.minutes,
            provider?.timezone || masterTz
        );

        //Retrive the selected dateTime formatted to locations timezone
        const locTzDate = timezoneConversion(
            newDate,
            selectedLocation && selectedLocation?.id !== CLIENT_LOCATION_ID
                ? selectedLocation?.timezone
                : provider?.timezone || masterTz
        );
        const locationTzDateInfo = formatDateInfoAttributes(
            locTzDate,
            selectedService?.minutes,
            selectedLocation && selectedLocation?.id !== CLIENT_LOCATION_ID
                ? selectedLocation?.timezone
                : provider?.timezone || masterTz
        );

        setDetail({
            sessionDateTime: {
                index: index,
                dateTime: dateTimeValue,
                providerTzDateTimeIso,
                providerTzAbbr,
                locationTzDateTimeIso: locationTzDateInfo.dtstamp_str,
                locationTzDateInfo
            },
            ...(slotType && { slotType })
        });
        setOpenSuggestedTimesDialog(false);
        setShowWhoIsAvailableDialog(false);
    }

    function handleWhoIsAvailableDateTimeChange(
        detailValue,
        slotType = SLOT_TYPES.SUGGESTED_SLOT,
        index = sessionIndexRef.current ?? 0
    ) {
        index = index ?? 0; //index cannot be null or undefined
        if (isRepeating) {
            setDetail({
                recurringSessionDateTime: {
                    slots: detailValue,
                    isSessionDateTimeOptimized: true
                },
                slotType
            });
        } else {
            let newDate = utcToZonedTime(
                new Date(detailValue),
                whoIsAvailableTimezone
            );

            setDetail({
                sessionDateTime: {
                    index: index,
                    dateTime: newDate,
                    isSessionDateTimeOptimized: true
                },
                slotType
            });
        }

        setOpenSuggestedTimesDialog(false);
        setShowWhoIsAvailableDialog(false);
        setWhoIsAvailableSlots([]);
        setWhoIsAvailableTimezone(null);
    }

    async function determineScheduleIdForClustering() {
        const matchedSBs = [];
        if (selectedLocation.id !== CLIENT_LOCATION_ID || placeId) {
            if (selectedLocation.id !== "0" && schedules && schedules.length) {
                for (let schedule of schedules) {
                    if (
                        schedule.active !== false &&
                        schedule.locations.includes(`CL-${selectedLocation.id}`)
                    )
                        matchedSBs.push(
                            ...schedule.tbs?.filter((sb) => sb.active !== false)
                        );
                }
            } else if (selectedLocation.id === "0") {
                //remote location
                const locations = await graphql(
                    graphqlOperation(locationByProvider, {
                        providerId: provider.id,
                        limit: 50
                    })
                );
                let providerLocations = locations.data.locationByProvider.items;
                for (let providerLocation of providerLocations) {
                    for (let schedule of schedules) {
                        if (
                            providerLocation.active !== false &&
                            schedule.active !== false &&
                            schedule.locations.includes(
                                `PL-${providerLocation.id}`
                            )
                        ) {
                            // not matching service. As long as schedule matches location, we are good.
                            if (
                                isAddressInTravelZone(
                                    {
                                        lat: clientAddress
                                            ? clientAddress.latitude
                                            : selectedLocation.latitude,
                                        lng: clientAddress
                                            ? clientAddress.longitude
                                            : selectedLocation.longitude
                                    },
                                    providerLocation
                                )
                            )
                                matchedSBs.push(
                                    ...schedule.tbs?.filter(
                                        (sb) => sb.active !== false
                                    )
                                );
                        }
                    }
                }
            }
            if (matchedSBs.length) setMatchedSBsForClusters(matchedSBs);
            else {
                // get earlier start time and latest end time from existing SBs
                if (schedules.length) {
                    const allSbs = schedules.flatMap(
                        (schedule) => schedule.tbs
                    );
                    const range = allSbs.reduce(
                        (range, sb) => ({
                            earliest:
                                sb.startTime < range.earliest
                                    ? sb.startTime
                                    : range.earliest,
                            latest:
                                sb.endTime > range.latest
                                    ? sb.endTime
                                    : range.latest
                        }),
                        { earliest: "23:55", latest: "00:00" }
                    );
                    setMatchedSBsForClusters([
                        {
                            ...DEFAULT_SB_FOR_CLUSTERS,
                            startTime: range.earliest,
                            endTime: range.latest
                        }
                    ]);
                } else setMatchedSBsForClusters([DEFAULT_SB_FOR_CLUSTERS]);
            }
        } else setMatchedSBsForClusters([DEFAULT_SB_FOR_CLUSTERS]); // no matching schedule, still go ahead with full availability for clusters
    }

    //handles opening the "Suggested times Dialog" or regular choose time dialog
    async function handleOpenDateTimePicker() {
        if (clusteringIsEnabled) {
            setOpenSuggestedTimesDialog(true);
        } else setOpenDateTimePicker(true);
    }

    const handleProviderChange = (providerId, handleAutoFocus = true) => {
        setDetail({
            providerId
        });
        if (handleAutoFocus) {
            setDateTimePickerAutoFocus(true);
        }
    };

    const handleProceedToCheckout = async () => {
        setIsFetching(true);
        //If remote booking, get providerLocation OR create a dummy one
        let providerLocation;
        if (isRemoteBooking) {
            providerLocation = await getOrCreateProviderLocation({
                address: {
                    ...selectedLocation,
                    coords: {
                        lat: selectedLocation.latitude,
                        lng: selectedLocation.longitude
                    },
                    prov: selectedLocation.state
                },
                provider
            });
            setDetail({
                providerLocation
            });
        }

        const {
            countrycode3166alpha2: countryShort,
            state,
            postalcode,
            id: locationId
        } = selectedLocation;
        try {
            const promises = [
                // get pricing
                selectedPackage && selectedPackage.type === PACKAGE_TYPE_DOLLAR
                    ? selectedPackage.servicetype.price
                    : getPrice(
                          {
                              isVirtual: selectedLocation.virtual,
                              countryShort,
                              province: state,
                              company,
                              postalCode: postalcode
                          },
                          selectedService
                      ),
                // create admin adhoc session
                createNewUiSession({
                    eventName: "NEW:AdminAdhoc",
                    companyId
                })
            ];

            //check card expiry status, only if company accepts payments
            //If company does not accept payments, we will not check card expiry and set payType to PAY_TYPE_COLLECT_LATER
            let cardExpiration;
            if (companyCollectsPayment) {
                cardExpiration = await checkCardExpiration({
                    stripecustomerid: client.stripeCustomerId,
                    companyId: client.companyId,
                    clientEmailAddress: client.user.emailaddress,
                    stripeAccount: company.stripeAccount,
                    stripeConnectEnabled: company.stripeConnectEnabled
                });
            } else {
                if (!selectedPackage || selectedPackage?.status !== "paid") {
                    setDetail({ payType: PAY_TYPE_COLLECT_LATER });
                }
            }

            //add heldSlots promise only if sessions exist
            if (sessions?.length > 0 && !(isRepeating && !selectedPackage)) {
                const sessionsToHold =
                    isRepeating && !selectedPackage ? [sessions[0]] : sessions;

                promises.push(
                    Promise.all(
                        sessionsToHold.map(
                            ({
                                dateTime,
                                locationTzDateTimeIso,
                                locationTzDate,
                                locationTzTime
                            }) =>
                                createProviderScheduleBK({
                                    companyId,
                                    locationId: isRemoteBooking
                                        ? `PL-${providerLocation?.id}`
                                        : `CL-${locationId}`,
                                    latitude: selectedLocation.latitude,
                                    longitude: selectedLocation.longitude,
                                    providerId,
                                    startDate: locationTzDate
                                        ? locationTzDate
                                        : getAWSDate(dateTime),
                                    startTime: locationTzTime
                                        ? locationTzTime
                                        : getBookingTimeOfAppt(dateTime),
                                    endTime: locationTzDateTimeIso
                                        ? addMinutesToTimeString(
                                              locationTzTime
                                                  ? locationTzTime
                                                  : getBookingTimeOfAppt(
                                                        dateTime
                                                    ),
                                              selectedService?.minutes
                                          )
                                        : getBookingTimeOfAppt(
                                              addMinutes(
                                                  dateTime,
                                                  selectedService?.minutes
                                              )
                                          ),
                                    type: "BOOKED",
                                    status: "PENDING",
                                    sdtutc: locationTzDateTimeIso
                                        ? locationTzDateTimeIso
                                        : format(
                                              dateTime,
                                              "yyyy-MM-dd'T'HH:mm:ssXXX"
                                          ),
                                    tz:
                                        selectedLocation?.id !==
                                        CLIENT_LOCATION_ID
                                            ? selectedLocation.timezone
                                            : provider.timezone
                                })
                        )
                    )
                );
            }

            const [regionalServicePrice, uiSessionId, heldSlots] =
                await Promise.all(promises); // will be undefined if sessions.length was 0,

            if (cardExpiration && cardExpiration.cardExpired) {
                setDetail({ cardExpired: true });
            }
            if (
                companyCollectsPayment &&
                (!client?.defaultpartialcc || cardExpiration?.cardExpired)
            ) {
                setDetail({ payType: PAY_TYPE_NEW_CC });
            }
            if (isRepeating && selectedService.price === 0) {
                setDetail({ payType: PAY_TYPE_NOT_REQUIRED });
            }
            setDetail({ price: regionalServicePrice });
            setDetail({ uiSessionId });
            setDetail({ heldSlots });

            //determine currency if it needs to be "currency by location" or "currency by company"
            const resolvedCurrency =
                company.currencyBasedOnLocation &&
                selectedLocation?.countrycode3166alpha2
                    ? getCurrencyForCountry(
                          selectedLocation.countrycode3166alpha2
                      )
                    : company?.currency;
            setDetail({ currency: resolvedCurrency });
            setIsFetching(false);
            showCheckout();
            await postApi("bookingapi", "/pendingslotstracking", {
                body: {
                    heldSlots,
                    uiSessionId: uiSessionId,
                    companyId: company.id
                }
            });
        } catch (e) {
            console.log(e);
        }
    };
    return (
        <>
            <Snackbar
                anchorOrigin={{
                    vertical: "top",
                    horizontal: "center"
                }}
                open={showMessage}
                autoHideDuration={5000}
                onClose={() => setShowMessage(false)}
                ContentProps={{
                    "aria-describedby": "message-id"
                }}
                message={<span id="message-id">{message}</span>}
            />
            <DialogTitle
                sx={{ paddingBottom: "32px" }}
            >{`Add booking ${selectedService ? `– ${currencyFormat(selectedService?.price, companyCurrency)}` : ""}`}</DialogTitle>
            <DialogContent sx={{ paddingBottom: "2px" }}>
                <Grid container spacing={2.25}>
                    <Grid item xs={12} pt={`0px !important`} pb={`18px`}>
                        <Alert
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                padding: "10px 0px",
                                border: `1px solid ${theme.palette.info.main}`,
                                marginBottom: "0px",
                                backgroundColor: theme.palette.info.lightest,
                                textAlign: "center"
                            }}
                            severity="info"
                            icon={<InfoIcon />}
                        >
                            <Typography
                                color={theme.palette.info.main}
                                fontWeight={500}
                                display={"inline"}
                            >
                                WARNING: manual bookings can{" "}
                                <span style={{ textDecoration: "underline" }}>
                                    override all schedules
                                </span>{" "}
                                – make sure the provider is available
                            </Typography>
                        </Alert>
                    </Grid>
                    <Grid item xs={12}>
                        <Grid item xs={12} md={6} width={"330px"}>
                            <ClientSelector
                                setClient={(client) => {
                                    setDetail({ client });
                                    if (client && !selectedService) {
                                        setServiceSelectorAutoFocus(true);
                                    } else if (client && !provider) {
                                        setProviderSelectorAutoFocus(true);
                                    } else {
                                        clientSelectorRef.current.blur();
                                    }
                                }}
                                margin="0px"
                                size="small"
                                hideInputEmailDisplay={true}
                                disableElements={isFetching}
                                autoFocus={!client}
                                inputRef={clientSelectorRef}
                                focus={clientSelectorAutoFocus}
                                placeholder="Type to search"
                                required={false}
                            />
                        </Grid>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                            <InputLabel
                                id="location-select-label"
                                size="small"
                                disabled={
                                    isFetching || activeLocations?.length === 0
                                }
                            >
                                Location
                            </InputLabel>
                            <Select
                                fullWidth
                                label="Location"
                                labelId="location-select-label"
                                name="location"
                                disabled={
                                    isFetching || activeLocations?.length === 0
                                }
                                value={selectedLocationId}
                                onChange={({ target: { value: locationId } }) =>
                                    setDetail({
                                        selectedLocationId: locationId
                                    })
                                }
                            >
                                <Divider
                                    sx={{
                                        paddingTop: "8px",
                                        borderColor: "white"
                                    }}
                                />
                                <MenuItem
                                    key={CLIENT_LOCATION_ID}
                                    value={CLIENT_LOCATION_ID}
                                >
                                    {locations[0].locationname}
                                </MenuItem>
                                <Divider />
                                {locations
                                    .slice(1)
                                    .map(({ id, locationname }) => (
                                        <MenuItem
                                            key={id}
                                            value={id}
                                            sx={{
                                                color:
                                                    selectedService &&
                                                    !providerLocationIds?.includes(
                                                        "CL-" + id
                                                    ) &&
                                                    id !== CLIENT_LOCATION_ID
                                                        ? theme.palette
                                                              .helperText.main
                                                        : ""
                                            }}
                                        >
                                            <Tooltip
                                                title={locationname}
                                                disableHoverListener={
                                                    locationname.length < 50
                                                }
                                                placement="right"
                                            >
                                                <Box
                                                    sx={{
                                                        display: "-webkit-box",
                                                        WebkitLineClamp: 1,
                                                        WebkitBoxOrient:
                                                            "vertical",
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis"
                                                    }}
                                                >
                                                    <Typography
                                                        variant="inherit"
                                                        component="span"
                                                    >
                                                        {locationname}
                                                    </Typography>
                                                </Box>
                                            </Tooltip>
                                        </MenuItem>
                                    ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        {selectedLocationId === CLIENT_LOCATION_ID ? (
                            <ClientAddressInput
                                setAddress={(value) => {
                                    setDetail({
                                        clientAddress: {
                                            addressoneline:
                                                value.addressOneLine,
                                            country: value.country,
                                            countrycode3166alpha2:
                                                value.countryShortName,
                                            id: CLIENT_LOCATION_ID,
                                            latitude: value.coords.lat,
                                            longitude: value.coords.lng,
                                            locationname: value.addressOneLine,
                                            state: value.prov,
                                            city: value.city,
                                            postalcode: value.postalCode,
                                            isRemoteLocation: true,
                                            isVirtual: false
                                        }
                                    });
                                }}
                                formWidth={"100%"}
                                formMargin={"0px"}
                                formMarginLeft={"0px"}
                                label={"Address"}
                                addressOneLine={
                                    selectedLocation?.addressoneline || ""
                                }
                                /* inputDisplay={clientAddressOneLine || null} */
                                autocompleteService={autocompleteService}
                                smallSize={true}
                                error={addressError}
                                setAddressError={setAddressError}
                                setPid={(value) => {
                                    setDetail({
                                        placeId: value
                                    });
                                }}
                                onClear={() =>
                                    setDetail({
                                        clientAddress: null
                                    })
                                }
                                disableAddress={isFetching}
                                sx={{}}
                            />
                        ) : (
                            <TextField
                                value={selectedLocation?.addressoneline || ""}
                                disabled
                                label="Address"
                            />
                        )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Autocomplete
                            id="select-service"
                            disableClearable
                            size="small"
                            options={services}
                            disabled={isFetching}
                            focus={serviceSelectorAutoFocus}
                            openOnFocus={true}
                            getOptionLabel={(option) =>
                                option.name ? option.name : ""
                            }
                            isOptionEqualToValue={(option, value) => {
                                return option.name === value.name;
                            }}
                            variant="outlined"
                            value={selectedService || null}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Service"
                                    InputProps={{
                                        ...params.InputProps
                                    }}
                                    InputLabelProps={{
                                        ...params.InputLabelProps
                                    }}
                                    variant="outlined"
                                    fullWidth
                                    inputRef={serviceSelectorRef}
                                />
                            )}
                            renderOption={(props, option) => (
                                <li {...props}>
                                    <Tooltip
                                        title={option.name}
                                        disableHoverListener={
                                            option.name.length < 50
                                        }
                                        placement="right"
                                    >
                                        <Box
                                            sx={{
                                                display: "-webkit-box",
                                                WebkitLineClamp: 1,
                                                WebkitBoxOrient: "vertical",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis"
                                            }}
                                        >
                                            <Typography
                                                variant="inherit"
                                                component="span"
                                            >
                                                {`${
                                                    !jsonParse(
                                                        option.locations,
                                                        []
                                                    ).includes(
                                                        selectedLocationId
                                                    ) &&
                                                    selectedLocationId !==
                                                        CLIENT_LOCATION_ID
                                                        ? "*"
                                                        : ""
                                                }${option.name}`}
                                            </Typography>
                                        </Box>
                                    </Tooltip>
                                </li>
                            )}
                            onChange={(e, value) => {
                                setDetail({
                                    selectedServiceId: value.id
                                });
                                if (!provider) {
                                    setProviderSelectorAutoFocus(true);
                                } else {
                                    setDateTimePickerAutoFocus(true);
                                }
                            }}
                        />
                    </Grid>
                    {servicePackages?.length > 0 && (
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <Autocomplete
                                    id="package"
                                    name="package"
                                    size="small"
                                    options={packageOptions}
                                    disabled={isFetching}
                                    getOptionLabel={(option) =>
                                        option ? option.label : ""
                                    }
                                    onChange={(event, value) =>
                                        setDetail({
                                            selectedPackage:
                                                value === null
                                                    ? null
                                                    : find(servicePackages, [
                                                          "id",
                                                          value.id
                                                      ]) ||
                                                      find(clientPackages, [
                                                          "id",
                                                          value.id
                                                      ])
                                        })
                                    }
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            variant="outlined"
                                            fullWidth
                                            label={
                                                selectedPackage
                                                    ? "Package (optional)"
                                                    : "Select package (optional)"
                                            }
                                        />
                                    )}
                                    renderOption={(
                                        props,
                                        option,
                                        { index }
                                    ) => (
                                        <>
                                            <li
                                                {...props}
                                                style={{ minHeight: "33px" }}
                                            >
                                                <Grid
                                                    container
                                                    direction="column"
                                                    sx={{ width: "100%" }}
                                                >
                                                    <Typography
                                                        noWrap
                                                        sx={{
                                                            lineHeight: "16px",
                                                            overflow: "hidden",
                                                            textOverflow:
                                                                "ellipsis",
                                                            whiteSpace:
                                                                "nowrap",
                                                            width: "100%"
                                                        }}
                                                    >
                                                        {option.title}
                                                        {option.subtitle && (
                                                            <Typography
                                                                variant="body1"
                                                                sx={{
                                                                    lineHeight:
                                                                        "16px",
                                                                    width: "100%"
                                                                }}
                                                            >
                                                                {
                                                                    option.subtitle
                                                                }
                                                            </Typography>
                                                        )}
                                                        {option.helperText && (
                                                            <Typography
                                                                variant="helperText"
                                                                sx={{
                                                                    lineHeight:
                                                                        "14px"
                                                                }}
                                                            >
                                                                {
                                                                    option.helperText
                                                                }
                                                            </Typography>
                                                        )}
                                                    </Typography>
                                                </Grid>
                                            </li>
                                            {index ===
                                                clientPackages?.length - 1 && (
                                                <Divider />
                                            )}
                                        </>
                                    )}
                                />
                            </FormControl>
                        </Grid>
                    )}
                    {selectedService &&
                        !jsonParse(selectedService.locations, []).includes(
                            selectedLocationId
                        ) &&
                        selectedLocationId !== CLIENT_LOCATION_ID && (
                            <Grid xs={12} pl={"18px"}>
                                <FormHelperText
                                    error
                                    sx={{ lineHeight: "14px" }}
                                >
                                    {
                                        "* This service is not available at the selected location"
                                    }
                                </FormHelperText>
                            </Grid>
                        )}
                    <Grid item xs={12} sx={{ display: "flex" }}>
                        <RRuleSelector
                            packageView={selectedPackage ? true : false}
                            submitting={isFetching}
                            checked={isRepeating}
                            onChange={() =>
                                setDetail({
                                    isRepeating: !isRepeating
                                })
                            }
                            fullWidth={selectedPackage || isRepeating}
                        />
                        {!selectedPackage && !isRepeating && (
                            <InfoTooltip title="Recurring bookings are NOT packages" />
                        )}
                    </Grid>
                    <Grid item xs={12} display={"flex"} gap={"12px"}>
                        <Button
                            variant={
                                clusteringIsEnabled
                                    ? "contained"
                                    : "containedDisabled"
                            }
                            onClick={
                                clusteringIsEnabled
                                    ? () => setShowWhoIsAvailableDialog(true)
                                    : () => setOpenFeatureGateDialog(true)
                            }
                            disabled={isFetching}
                        >
                            Dispatcher Co-pilot
                        </Button>
                        <InfoTooltip title="Use the Smart Clusters functionality to find the most suitable provider for the selected service and location" />
                    </Grid>

                    {/* {selectedPackage && (
                        <Grid container item xs={12}>
                            <FormControl fullWidth>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            name="switch-no-sessions"
                                            checked={isBookSeparately}
                                            onChange={({
                                                target: { checked }
                                            }) => {
                                                setDetail({
                                                    isBookSeparately: checked
                                                });
                                            }}
                                            value={true}
                                            color="primary"
                                            inputProps={{
                                                "aria-label": "primary checkbox"
                                            }}
                                        />
                                    }
                                    label="Client will book sessions separately"
                                />
                            </FormControl>
                        </Grid>
                    )} */}
                    {sessions && (
                        <>
                            {(isRepeating && !selectedPackage
                                ? [sessions[0]]
                                : sessions
                            ).map(({ dateTime, providerId }, i) => (
                                <React.Fragment key={i}>
                                    <Grid item xs={12} sm={6}>
                                        <Autocomplete
                                            id="select-user"
                                            disableClearable
                                            loadingText="Loading..."
                                            size="small"
                                            loading={!providersList}
                                            options={providersList}
                                            disabled={i !== 0 || isFetching}
                                            focus={providerSelectorAutoFocus}
                                            getOptionLabel={(option) =>
                                                option.name
                                                    ? option.name
                                                    : `${option.firstname} ${option.lastname}`
                                            }
                                            isOptionEqualToValue={(
                                                option,
                                                value
                                            ) => {
                                                return (
                                                    option.name === value.name
                                                );
                                            }}
                                            variant="outlined"
                                            value={provider || null}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label={
                                                        provider
                                                            ? "Provider"
                                                            : "Select provider"
                                                    }
                                                    InputProps={{
                                                        ...params.InputProps
                                                    }}
                                                    InputLabelProps={{
                                                        ...params.InputLabelProps
                                                    }}
                                                    variant="outlined"
                                                    fullWidth
                                                    inputRef={
                                                        providerSelectorRef
                                                    }
                                                />
                                            )}
                                            renderOption={(props, option) => (
                                                <li {...props}>
                                                    <Grid
                                                        container
                                                        direction="column"
                                                        sx={{ width: "100%" }}
                                                    >
                                                        <Typography
                                                            noWrap
                                                            sx={{
                                                                lineHeight:
                                                                    "14px",
                                                                overflow:
                                                                    "hidden",
                                                                textOverflow:
                                                                    "ellipsis",
                                                                whiteSpace:
                                                                    "nowrap",
                                                                width: "100%"
                                                            }}
                                                        >
                                                            {option.name}
                                                        </Typography>
                                                        <Typography
                                                            noWrap
                                                            variant="helperText"
                                                            sx={{
                                                                lineHeight:
                                                                    "14px",
                                                                overflow:
                                                                    "hidden",
                                                                textOverflow:
                                                                    "ellipsis",
                                                                whiteSpace:
                                                                    "nowrap",
                                                                width: "100%"
                                                            }}
                                                        >
                                                            {option.email}
                                                        </Typography>
                                                    </Grid>
                                                </li>
                                            )}
                                            onChange={(e, value) => {
                                                handleProviderChange(value.id);
                                            }}
                                        />
                                    </Grid>
                                    <Grid
                                        item
                                        xs={12}
                                        sm={6}
                                        display={"flex"}
                                        alignItems={"center"}
                                        gap={"10px"}
                                    >
                                        <LocalizationProvider
                                            dateAdapter={AdapterDateFns}
                                            adapterLocale={enUS}
                                        >
                                            <MobileDateTimePicker
                                                minutesStep={5}
                                                open={false}
                                                inputRef={dateTimePickerRef}
                                                onClose={() => {
                                                    setOpenDateTimePicker(
                                                        false
                                                    );
                                                    if (
                                                        openDateTimePickerView ===
                                                        "hours"
                                                    )
                                                        setOpenDateTimePickerView(
                                                            "day"
                                                        );
                                                }}
                                                views={[
                                                    "year",
                                                    "month",
                                                    "day",
                                                    "hours",
                                                    "minutes"
                                                ]}
                                                sx={{
                                                    position: "relative",
                                                    width: "100%"
                                                }}
                                                label="Booking date and time"
                                                inputFormat={dateInputFormat}
                                                minDateMessage={
                                                    "Please select a start date."
                                                }
                                                value={
                                                    dateTime
                                                        ? dateTime
                                                        : getCurrentTimeAdjustedToNextHour()
                                                }
                                                onChange={(newValue) => {
                                                    handleDateTimeChange(
                                                        newValue
                                                    );
                                                }}
                                                onOpen={async () => {
                                                    sessionIndexRef.current = i;
                                                    await handleOpenDateTimePicker();
                                                }}
                                                openTo={openDateTimePickerView}
                                                renderInput={(params) => (
                                                    <TextField
                                                        error={
                                                            clusteringIsEnabled &&
                                                            suggestionsEnabled &&
                                                            matchedSBsForClusters?.length
                                                        }
                                                        fullWidth
                                                        {...params}
                                                        InputProps={{
                                                            endAdornment: (
                                                                <InputAdornment position="end">
                                                                    <>
                                                                        {provider && (
                                                                            <Typography
                                                                                variant="body1"
                                                                                mr={
                                                                                    "2px"
                                                                                }
                                                                                color={
                                                                                    theme
                                                                                        .palette
                                                                                        .grey
                                                                                        .medium
                                                                                }
                                                                            >
                                                                                {`(${sessions[i]?.providerTzAbbr})`}
                                                                            </Typography>
                                                                        )}
                                                                    </>
                                                                </InputAdornment>
                                                            )
                                                        }}
                                                    />
                                                )}
                                                PopperProps={{
                                                    disablePortal: true,
                                                    placement: "bottom-end"
                                                }}
                                                disabled={
                                                    disableElements ||
                                                    isFetching
                                                }
                                            />
                                        </LocalizationProvider>
                                        {i !== 0 && (
                                            <DeleteIcon
                                                type="button"
                                                variant="deleteIcon"
                                                onClick={() => {
                                                    sessions.splice(i, 1); //mutation
                                                    setDetail({
                                                        sessions
                                                    });
                                                }}
                                            />
                                        )}
                                    </Grid>
                                </React.Fragment>
                            ))}
                            {isRepeating && !selectedPackage && (
                                <Grid item xs={12}>
                                    <Typography
                                        variant="subHeading2"
                                        sx={{ mb: 1 }}
                                    >
                                        {sessions.length === 1
                                            ? "Here is the upcoming session"
                                            : `Here are the upcoming ${sessions.length > 10 ? "10" : sessions.length} sessions`}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                    >
                                        {sessions
                                            ?.slice(0, 10)
                                            .map((session, index) => (
                                                <div key={index}>
                                                    {format(
                                                        session.dateTime,
                                                        provider &&
                                                            sessions.length
                                                            ? `EEE, MMM d, yyyy '–' h:mm a '(${sessions[0]?.providerTzAbbr})'`
                                                            : "EEE, MMM d, yyyy '–' h:mm a"
                                                    )}
                                                </div>
                                            ))}
                                    </Typography>
                                </Grid>
                            )}
                            {selectedPackage &&
                                sessions !== null &&
                                sessions.length <
                                    selectedPackageRemainingQuantity && (
                                    <Grid item xs={12}>
                                        <Button
                                            variant="outlined"
                                            startIcon={<AddIcon />}
                                            disabled={isFetching}
                                            onClick={() => {
                                                setDetail({
                                                    sessions: [
                                                        ...(sessions || []),
                                                        {
                                                            providerId,
                                                            dateTime:
                                                                sessions.length >
                                                                0
                                                                    ? new Date(
                                                                          new Date(
                                                                              sessions[
                                                                                  sessions.length -
                                                                                      1
                                                                              ].dateTime
                                                                          ).setDate(
                                                                              new Date(
                                                                                  sessions[
                                                                                      sessions.length -
                                                                                          1
                                                                                  ].dateTime
                                                                              ).getDate() +
                                                                                  7
                                                                          )
                                                                      )
                                                                    : getCurrentTimeAdjustedToNextHour()
                                                        }
                                                    ]
                                                });
                                                handleDateTimeChange(
                                                    sessions.length > 0
                                                        ? new Date(
                                                              new Date(
                                                                  sessions[
                                                                      sessions.length -
                                                                          1
                                                                  ].dateTime
                                                              ).setDate(
                                                                  new Date(
                                                                      sessions[
                                                                          sessions.length -
                                                                              1
                                                                      ].dateTime
                                                                  ).getDate() +
                                                                      7
                                                              )
                                                          )
                                                        : getCurrentTimeAdjustedToNextHour(),
                                                    sessions.length
                                                );
                                            }}
                                        >
                                            Add Session
                                        </Button>
                                    </Grid>
                                )}
                        </>
                    )}

                    <Grid
                        item
                        xs={12}
                        pb={"1px"}
                        sx={{ display: "flex", gap: "6px" }}
                    >
                        <Button
                            name="switch-notes"
                            disabled={isFetching}
                            onClick={() =>
                                setDetail({
                                    showNotes: !showNotes
                                })
                            }
                            variant="text"
                            color="primary"
                            disableRipple
                            endIcon={
                                showNotes ? (
                                    <ExpandLessIcon />
                                ) : (
                                    <ExpandMoreIcon />
                                )
                            }
                            sx={{ textTransform: "none" }}
                        >
                            {showNotes
                                ? `Hide booking notes`
                                : `Show booking notes`}
                        </Button>
                        <InfoTooltip title="These notes are public and visible to admins, managers, providers and clients" />
                    </Grid>
                    {showNotes && (
                        <Grid item xs={12} pt="0px !important">
                            <LinkDetectionTextField
                                label="Booking notes"
                                labelWidth="80px"
                                value={notes}
                                onChange={(newValue) =>
                                    setDetail({ notes: newValue })
                                }
                            />
                        </Grid>
                    )}
                </Grid>

                <CustomDateTimeDialog open={showCustomDateTimeDialog} />
                <Dialog open={showChangeRepeatingDialog}>
                    <DialogTitle>
                        Change to{" "}
                        {isRepeating ? "single sessions" : "repeating sessions"}
                    </DialogTitle>
                    <DialogContent>
                        All current time slots will be removed – do you wish to
                        continue?
                    </DialogContent>
                    <DialogActions>
                        <Button
                            onClick={() => setShowCustomDateTimeDialog(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={() => setShowCustomDateTimeDialog(false)}
                        >
                            Change to {isRepeating ? "single" : "repeating"}
                        </Button>
                    </DialogActions>
                </Dialog>
                {showWhoIsAvailableDialog && (
                    <WhoIsAvailableDialog
                        open={showWhoIsAvailableDialog}
                        onClose={() => {
                            if (showOnlyWhoIsAvailableDialog) {
                                setShowBookingDetails(false);
                                setTimeout(() => {
                                    setShowOnlyWhoIsAvailableDialog(false);
                                }, 100);
                            }
                            setShowWhoIsAvailableDialog(false);
                            setOpenSuggestedTimesDialog(false);
                        }}
                        onDateTimeChange={(
                            newValue,
                            slotType = SLOT_TYPES.SUGGESTED_SLOT
                        ) => {
                            handleWhoIsAvailableDateTimeChange(
                                newValue,
                                slotType,
                                sessionIndexRef.current
                            );
                        }}
                        handleProviderChange={(providerId) => {
                            handleProviderChange(providerId, false);
                        }}
                        handleClientAutoFocus={() => {
                            if (!client) {
                                setClientSelectorAutoFocus(true);
                            }
                        }}
                    />
                )}
            </DialogContent>
            <DialogActions sx={{ paddingTop: "34px", gap: "10px" }}>
                <Button
                    variant="outlined"
                    onClick={onClose}
                    disabled={isFetching}
                >
                    Cancel
                </Button>
                <LoadingButton
                    variant="contained"
                    disabled={!canCheckout || isFetching || checkoutOpen}
                    onClick={handleProceedToCheckout}
                    loading={isFetching}
                >
                    Checkout
                </LoadingButton>
            </DialogActions>
            {openSuggestedTimesDialog && (
                <SuggestedTimesDialog
                    open={openSuggestedTimesDialog}
                    onClose={handleCloseSuggestedTimesDialog}
                    sessionIndex={sessionIndexRef.current}
                    handleSelectedCustomDateTime={() => {
                        setOpenDateTimePickerView("day");
                        setOpenDateTimePicker(true);
                        setOpenSuggestedTimesDialog(false);
                    }}
                    onDateTimeChange={(
                        newValue,
                        slotType = SLOT_TYPES.SUGGESTED_SLOT
                    ) => {
                        handleWhoIsAvailableDateTimeChange(
                            newValue,
                            slotType,
                            sessionIndexRef.current
                        );
                    }}
                    onRegularDateTimeChange={(newValue) => {
                        handleDateTimeChange(newValue);
                    }}
                    handleClientAutoFocus={() => {
                        if (!client) {
                            setClientSelectorAutoFocus(true);
                        }
                    }}
                />
            )}
            {openDateTimePicker && (
                <DateTimeSelector
                    open={openDateTimePicker}
                    onClose={() => setOpenDateTimePicker(false)}
                    onChange={(newValue) => {
                        handleDateTimeChange(newValue);
                    }}
                    initialDateTime={
                        sessions?.[sessionIndexRef.current]?.dateTime
                    }
                    timezone={provider ? provider.timezone : masterTz}
                    handleBackToSuggestedTimes={() => {
                        setOpenSuggestedTimesDialog(true);
                        setOpenDateTimePicker(false);
                    }}
                    showBackToSuggestedTimes={
                        matchedSBsForClusters?.length &&
                        !(sessions[0]?.dateTime < new Date())
                    }
                    showTimezone={provider ? true : false}
                />
            )}
        </>
    );
};

const CustomDateTimeDialog = ({ open }) => {
    const [customTimeActions] = useAtom(customTimeActionsAtom);
    const [{ date, time }, setDateTime] = useState({ date: null, time: null });
    return (
        <>
            <Dialog open={open}>
                <DialogTitle>Custom date and time</DialogTitle>
                <DialogContent>
                    <LocalizationProvider
                        dateAdapter={AdapterDateFns}
                        adapterLocale={customLocale}
                    >
                        <DatePicker
                            // autoFocus
                            label="Date"
                            renderInput={(props) => <TextField {...props} />}
                            value={date}
                            onChange={(date) =>
                                setDateTime({
                                    time: time,
                                    date
                                })
                            }
                        />
                        <TimePicker
                            label="time"
                            renderInput={(props) => <TextField {...props} />}
                            value={time}
                            onChange={(time) =>
                                setDateTime({
                                    date: date,
                                    time
                                })
                            }
                        />
                        <DialogActions>
                            <Button
                                onClick={() => {
                                    customTimeActions.cancel();
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                disabled={!date || !time}
                                onClick={() => {
                                    customTimeActions.save({ date, time });
                                }}
                            >
                                OK
                            </Button>
                        </DialogActions>
                    </LocalizationProvider>
                </DialogContent>
            </Dialog>
        </>
    );
};
